import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Keep the worker config in the same module where <Document> / <Page> are used.
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
function PDFViewer({
  file,
  onPageLoad,
  onDocumentLoad,
  onPageChange,
  children,
}) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setPdfUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setPdfUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const handleDocumentLoadSuccess = ({ numPages: total }) => {
    setNumPages(total);
    setPageNumber(1);
    onDocumentLoad?.({ numPages: total });
    onPageChange?.(1);
  };

  const handlePageLoadSuccess = (page) => {
    const viewportAtScaleOne =
      typeof page.getViewport === "function"
        ? page.getViewport({ scale: 1, rotation: 0 })
        : null;

    const originalWidth =
      typeof page.originalWidth === "number"
        ? page.originalWidth
        : viewportAtScaleOne?.width ?? page.width / scale;

    const originalHeight =
      typeof page.originalHeight === "number"
        ? page.originalHeight
        : viewportAtScaleOne?.height ?? page.height / scale;

    const renderedWidth =
      typeof page.width === "number" ? page.width : originalWidth * scale;

    const renderedHeight =
      typeof page.height === "number" ? page.height : originalHeight * scale;

    onPageLoad?.({
      pageNumber,
      width: renderedWidth,
      height: renderedHeight,
      originalWidth,
      originalHeight,
      scale,
      rotation: page.rotate ?? 0,
    });
  };

  const goToPrevPage = () => {
    const nextPage = Math.max(pageNumber - 1, 1);
    setPageNumber(nextPage);
    onPageChange?.(nextPage);
  };

  const goToNextPage = () => {
    const nextPage = Math.min(pageNumber + 1, numPages || 1);
    setPageNumber(nextPage);
    onPageChange?.(nextPage);
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const overlayChildren =
    typeof children === "function" ? children({ pageNumber, scale }) : children;

  if (!pdfUrl) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        Upload a PDF to start editing.
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f0f0f0",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: 10,
          backgroundColor: "#fff",
          borderBottom: "1px solid #ddd",
          flexWrap: "wrap",
        }}
      >
        <button onClick={zoomOut} className="btn">
          🔍 Zoom Out
        </button>

        <button onClick={zoomIn} className="btn">
          🔍 Zoom In
        </button>

        <span>Scale: {Math.round(scale * 100)}%</span>

        <button
          onClick={goToPrevPage}
          disabled={pageNumber <= 1}
          className="btn"
        >
          ◀ Previous
        </button>

        <span>
          Page {pageNumber} of {numPages || "?"}
        </span>

        <button
          onClick={goToNextPage}
          disabled={pageNumber >= numPages}
          className="btn"
        >
          Next ▶
        </button>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: 20,
        }}
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          loading={<div>Loading PDF...</div>}
          error={<div>Failed to load PDF.</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            onLoadSuccess={handlePageLoadSuccess}
            renderTextLayer
            renderAnnotationLayer
            loading={<div>Loading page...</div>}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
              }}
            >
              {overlayChildren}
            </div>
          </Page>
        </Document>
      </div>
    </div>
  );
}

export default PDFViewer;
